import { useEffect, useRef, useState, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { firebaseAuth } from '../firebase';
import { getPathLocale, detectPreferredLocale } from '../lib/locale';

const IDLE_TIMEOUT = 7 * 24 * 60 * 60 * 1000; // 7 days
const WARNING_BEFORE = 5 * 60 * 1000; // Show warning 5 minutes before logout

interface UseIdleTimeoutOptions {
  /** Callback before logout (e.g., to save drafts) */
  onBeforeLogout?: () => Promise<void> | void;
  /** Custom timeout in ms (default: 7 days) */
  timeout?: number;
}

/**
 * Hook to handle idle timeout with 7-day idle timeout
 * Shows warning before logout
 */
export const useIdleTimeout = ({
  onBeforeLogout,
  timeout = IDLE_TIMEOUT,
}: UseIdleTimeoutOptions = {}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const lastActivityRef = useRef<number>(Date.now());
  const warningIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    // Hide warning if user becomes active
    if (showWarning) {
      setShowWarning(false);
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
    }
  }, [showWarning]);

  // Extend session (called when user clicks "Stay Logged In")
  const extendSession = useCallback(() => {
    updateActivity();
    setShowWarning(false);
  }, [updateActivity]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      if (onBeforeLogout) {
        await onBeforeLogout();
      }
      await signOut(firebaseAuth);
      const locale = getPathLocale() ?? detectPreferredLocale();
      window.location.href = `/${locale}/login`;
    } catch (error) {
      console.error('[IdleTimeout] Logout error:', error);
    }
  }, [onBeforeLogout]);

  // Check idle time
  const checkIdleTime = useCallback(() => {
    const idleTime = Date.now() - lastActivityRef.current;
    const timeUntilLogout = timeout - idleTime;

    // Time to show warning
    if (timeUntilLogout <= WARNING_BEFORE && timeUntilLogout > 0) {
      if (!showWarning) {
        setShowWarning(true);
        setSecondsRemaining(Math.ceil(timeUntilLogout / 1000));

        // Start countdown
        warningIntervalRef.current = setInterval(() => {
          setSecondsRemaining((prev) => {
            if (prev <= 1) {
              logout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }

    // Time to logout
    if (timeUntilLogout <= 0) {
      logout();
    }
  }, [timeout, showWarning, logout]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      updateActivity();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also track visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkIdleTime();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateActivity, checkIdleTime]);

  // Set up check interval
  useEffect(() => {
    checkIntervalRef.current = setInterval(() => {
      checkIdleTime();
    }, 6 * 60 * 60 * 1000); // Check every 6 hours (since timeout is 7 days)

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
      }
    };
  }, [checkIdleTime]);

  return {
    showWarning,
    secondsRemaining,
    extendSession,
    logout,
  };
};

export default useIdleTimeout;
